import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import PageBanner from '../components/PageBanner';
import PageTransition from '../components/PageTransition';
import useScrollTop from '../hooks/useScrollTop';
import emailjs from '@emailjs/browser';
import '../styles/ContactPage.css';

const ContactPage = () => {
  useScrollTop();
  const form = useRef();
  
  // Get URL parameters
  const params = new URLSearchParams(window.location.search);
  const productFromUrl = params.get('product');
  const categoryFromUrl = params.get('category');
  
  const [formData, setFormData] = useState({
    name: '',
    companyName: '',
    email: '',
    phone: '',
    address: '',
    interestedProducts: productFromUrl ? `${productFromUrl} (${categoryFromUrl})` : '',
    expectedQuantity: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ type: '', message: '' });

  // Auto-dismiss toast after 5 seconds when a message is present
  useEffect(() => {
    if (!submitStatus.message) return;
    const timer = setTimeout(() => {
      setSubmitStatus({ type: '', message: '' });
    }, 5000);
    return () => clearTimeout(timer);
  }, [submitStatus.message]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const formField = name.replace('user_', '').replace('business_', '').replace('expected_quantity', 'expectedQuantity').replace('interested_products', 'interestedProducts').replace('company_name', 'companyName');
    setFormData(prevState => ({
      ...prevState,
      [formField]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus({ type: '', message: '' });

    try {
      // Send the email using EmailJS with form reference
      const response = await emailjs.sendForm(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        form.current,
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY
      );
      
      if (response.status !== 200) {
        throw new Error('Failed to send email');
      }

      // Clear form and show success message
      setFormData({
        name: '',
        companyName: '',
        email: '',
        phone: '',
        address: '',
        interestedProducts: '',
        expectedQuantity: '',
        message: ''
      });
      setSubmitStatus({
        type: 'success',
        message: 'Thank you! Your inquiry has been sent successfully. We will contact you soon.'
      });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.error('Error sending email:', error);
      setSubmitStatus({
        type: 'error',
        message: `Sorry, there was a problem sending your inquiry. ${
          !navigator.onLine 
            ? 'Please check your internet connection.' 
            : 'Please try again or contact us directly.'
        }`
      });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <PageTransition>
      <div className="contact-page">
      <PageBanner 
        title="Contact Us"
        description="Get in touch with us for quotes and inquiries"
      />
      {/* Top-right toast notification */}
      {submitStatus.message && (
        <div className="toast-container" aria-live="polite" aria-atomic="true">
          <div className={`toast ${submitStatus.type}`} role="status">
            <div className="toast-message">{submitStatus.message}</div>
            <button
              className="toast-close"
              onClick={() => setSubmitStatus({ type: '', message: '' })}
              aria-label="Close notification"
            >
              ×
            </button>
          </div>
        </div>
      )}
      
      <div className="breadcrumbs">
        <div className="breadcrumbs-content">
          <Link to="/" className="breadcrumb-item">Home</Link>
          <span className="breadcrumb-separator">/</span>
          <span className="breadcrumb-current">Contact Us</span>
        </div>
      </div>

      <div className="contact-container">
        <div className="contact-info">
          <h2>Let's Discuss Your Requirements</h2>
          <p>Please fill out the form below and we'll get back to you as soon as possible with pricing and availability.</p>
          
          <div className="company-details">
            <div className="info-item">
              <h3>Address</h3>
              <p>Township, Lahore, PK</p>
            </div>
            <div className="info-item">
              <h3>Email</h3>
              <p>malik_int@hotmail.com</p>
            </div>
            <div className="info-item">
              <h3>Phone</h3>
              <p>+92 336 0550065 (Waleed Malik - CEO)</p>
              <p>+92 300 4126361 (M. Sharif - Manager)</p>
            </div>
          </div>
        </div>

        <form ref={form} className="contact-form" onSubmit={handleSubmit}>
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="user_name">Full Name *</label>
              <input
                type="text"
                id="user_name"
                name="user_name"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="company_name">Company Name *</label>
              <input
                type="text"
                id="company_name"
                name="company_name"
                value={formData.companyName}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="user_email">Email Address *</label>
              <input
                type="email"
                id="user_email"
                name="user_email"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="user_phone">Phone Number *</label>
              <input
                type="tel"
                id="user_phone"
                name="user_phone"
                value={formData.phone}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group full-width">
              <label htmlFor="business_address">Business Address</label>
              <input
                type="text"
                id="business_address"
                name="business_address"
                value={formData.address}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label htmlFor="interested_products">Interested Products *</label>
              <input
                type="text"
                id="interested_products"
                name="interested_products"
                value={formData.interestedProducts}
                onChange={handleChange}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="expected_quantity">Expected Quantity</label>
              <input
                type="text"
                id="expected_quantity"
                name="expected_quantity"
                value={formData.expectedQuantity}
                onChange={handleChange}
                placeholder="e.g., 1000 kg"
              />
            </div>

            <div className="form-group full-width">
              <label htmlFor="message">Additional Details</label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                rows="4"
                placeholder="Please provide any specific requirements or questions"
              ></textarea>
            </div>
          </div>

          {/* in-form status removed: using top-right toast for notifications */}

          <button 
            type="submit" 
            className="submit-btn" 
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Sending...' : 'Send Inquiry'}
          </button>
        </form>
      </div>
    </div>
    </PageTransition>
  );
};

export default ContactPage;
